import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FunnelStep } from "@/types/analytics.types";

interface UseConversionFunnelOptions {
  startDate: string;
  endDate: string;
}

export const useConversionFunnel = ({ startDate, endDate }: UseConversionFunnelOptions) => {
  return useQuery({
    queryKey: ['conversion-funnel', startDate, endDate],
    queryFn: async (): Promise<FunnelStep[]> => {
      // Define funnel steps
      const steps = [
        { name: 'Seiten-Besuche', event: 'page_view' },
        { name: 'CTA geklickt', event: 'cta_click' },
        { name: 'Formular gestartet', event: 'form_start' },
        { name: 'Lead erfasst', event: 'lead_captured' },
        { name: 'Beratung angefragt', event: 'consultation_requested' },
      ];

      const funnelData: FunnelStep[] = [];
      let previousCount = 0;

      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        
        // For page_view, we can estimate from all events as not all page views might be tracked
        if (step.event === 'page_view') {
          // Count unique sessions
          const { data, error } = await supabase
            .from('analytics_events')
            .select('session_id')
            .gte('created_at', startDate)
            .lte('created_at', endDate);

          if (error) throw error;

          const uniqueSessions = new Set(data?.map(e => e.session_id).filter(Boolean));
          const count = uniqueSessions.size || 0;
          
          previousCount = count;
          funnelData.push({
            name: step.name,
            count,
            percentage: 100,
            dropoff: 0,
          });
        } else if (step.event === 'lead_captured') {
          // Use actual leads data
          const { count, error } = await supabase
            .from('checklist_leads')
            .select('*', { count: 'exact', head: true })
            .gte('created_at', startDate)
            .lte('created_at', endDate);

          if (error) throw error;

          const leadCount = count || 0;
          const percentage = previousCount > 0 ? (leadCount / previousCount) * 100 : 0;
          const dropoff = previousCount > 0 ? 100 - percentage : 0;

          funnelData.push({
            name: step.name,
            count: leadCount,
            percentage: Math.round(percentage),
            dropoff: Math.round(dropoff),
          });

          previousCount = leadCount;
        } else if (step.event === 'consultation_requested') {
          // Count consultation requests
          const { count: consultationCount, error } = await supabase
            .from('checklist_leads')
            .select('*', { count: 'exact', head: true })
            .eq('wants_consultation', true)
            .gte('created_at', startDate)
            .lte('created_at', endDate);

          if (error) throw error;

          const reqCount = consultationCount || 0;
          const percentage = previousCount > 0 ? (reqCount / previousCount) * 100 : 0;
          const dropoff = previousCount > 0 ? 100 - percentage : 0;

          funnelData.push({
            name: step.name,
            count: reqCount,
            percentage: Math.round(percentage),
            dropoff: Math.round(dropoff),
          });

          previousCount = reqCount;
        } else {
          // Count events from analytics_events table
          const { count, error } = await supabase
            .from('analytics_events')
            .select('*', { count: 'exact', head: true })
            .eq('event_name', step.event)
            .gte('created_at', startDate)
            .lte('created_at', endDate);

          if (error) throw error;

          const eventCount = count || 0;
          const percentage = previousCount > 0 ? (eventCount / previousCount) * 100 : 0;
          const dropoff = previousCount > 0 ? 100 - percentage : 0;

          funnelData.push({
            name: step.name,
            count: eventCount,
            percentage: Math.round(percentage),
            dropoff: Math.round(dropoff),
          });

          previousCount = eventCount;
        }
      }

      return funnelData;
    },
    refetchInterval: 60000, // Refetch every minute
  });
};
